// Public API client for candidate-facing interview endpoints.
// Separate from the authenticated Hono RPC client.

import { frontendConfig } from '@kiku/config/env/frontend';

const BASE_URL = `${frontendConfig.VITE_API_BASE_URL.replace(/\/$/, '')}/api/interview`;

export interface SessionInfo {
  status: 'pending' | 'in_progress' | 'completed' | 'expired';
  language: string | null;
  modality: string;
  feedbackSubmitted: boolean;
  job: {
    title: string;
    employer: string | null;
    languages: string[];
    modality: string[];
  };
  candidate: {
    name: string;
  };
}

export interface InterviewMessageResponse {
  id: string;
  role: 'assistant' | 'candidate';
  content: string;
  copyPasteDetected: boolean;
  createdAt: string;
}

export interface StartSessionResponse {
  greeting: string;
  messages: InterviewMessageResponse[];
}

interface ErrorBody {
  error?: string;
}

async function parseErrorBody(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as ErrorBody;
    return body.error ?? 'Unknown error';
  } catch {
    return 'Unknown error';
  }
}

export async function fetchSessionInfo(token: string): Promise<SessionInfo> {
  const res = await fetch(`${BASE_URL}/${token}`);
  if (!res.ok) {
    throw new InterviewApiError(res.status, await parseErrorBody(res));
  }
  return (await res.json()) as SessionInfo;
}

export async function startSession(token: string, language: string): Promise<StartSessionResponse> {
  const res = await fetch(`${BASE_URL}/${token}/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ language })
  });
  if (!res.ok) {
    throw new InterviewApiError(res.status, await parseErrorBody(res));
  }
  return (await res.json()) as StartSessionResponse;
}

export async function fetchMessages(
  token: string
): Promise<{ messages: InterviewMessageResponse[] }> {
  const res = await fetch(`${BASE_URL}/${token}/messages`);
  if (!res.ok) {
    throw new InterviewApiError(res.status, await parseErrorBody(res));
  }
  return (await res.json()) as { messages: InterviewMessageResponse[] };
}

export interface StreamCallbacks {
  onChunk: (text: string) => void;
  onDone: (completed: boolean) => void;
  onError: (error: string) => void;
}

function dispatchEvent(eventType: string, data: string, callbacks: StreamCallbacks): void {
  if (eventType === 'chunk') {
    callbacks.onChunk(data);
  } else if (eventType === 'done') {
    try {
      const parsed = JSON.parse(data) as { completed: boolean };
      callbacks.onDone(parsed.completed);
    } catch {
      callbacks.onDone(false);
    }
  } else if (eventType === 'error') {
    try {
      const parsed = JSON.parse(data) as { error: string };
      callbacks.onError(parsed.error);
    } catch {
      callbacks.onError(data);
    }
  }
}

export async function sendMessage(
  token: string,
  content: string,
  copyPasteDetected: boolean,
  callbacks: StreamCallbacks
): Promise<void> {
  const res = await fetch(`${BASE_URL}/${token}/message`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content, copyPasteDetected })
  });

  if (!res.ok) {
    callbacks.onError(await parseErrorBody(res));
    return;
  }

  const reader = res.body?.getReader();
  if (!reader) {
    callbacks.onError('No response body');
    return;
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let eventType = '';
  let dataLines: string[] = [];

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Parse SSE events from the buffer
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        if (line === '') {
          // Blank line = end of SSE event — dispatch accumulated data
          if (dataLines.length > 0) {
            dispatchEvent(eventType, dataLines.join('\n'), callbacks);
          }
          eventType = '';
          dataLines = [];
        } else if (line.startsWith('event: ')) {
          eventType = line.slice(7).trim();
        } else if (line.startsWith('data: ')) {
          dataLines.push(line.slice(6));
        }
      }
    }

    // Dispatch any event left in the buffer when the stream ends without a trailing blank line
    if (dataLines.length > 0) {
      dispatchEvent(eventType, dataLines.join('\n'), callbacks);
    }
  } finally {
    reader.releaseLock();
  }
}

export async function submitFeedback(
  token: string,
  thumbs: 'up' | 'down'
): Promise<{ id: string; thumbs: string; createdAt: string }> {
  const res = await fetch(`${BASE_URL}/${token}/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ thumbs })
  });
  if (!res.ok) throw new InterviewApiError(res.status, await parseErrorBody(res));
  return (await res.json()) as { id: string; thumbs: string; createdAt: string };
}

export async function addFeedbackNotes(token: string, notes: string): Promise<void> {
  const res = await fetch(`${BASE_URL}/${token}/feedback`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ notes })
  });
  if (!res.ok) throw new InterviewApiError(res.status, await parseErrorBody(res));
}

export async function restartInterview(token: string, notes: string): Promise<{ token: string }> {
  const res = await fetch(`${BASE_URL}/${token}/restart`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ notes })
  });
  if (!res.ok) throw new InterviewApiError(res.status, await parseErrorBody(res));
  return res.json() as Promise<{ token: string }>;
}

export interface VoiceConnectResponse {
  participantToken: string;
  livekitUrl: string;
}

export async function connectVoiceInterview(
  token: string,
  language: string
): Promise<VoiceConnectResponse> {
  const res = await fetch(`${BASE_URL}/${token}/voice/connect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ language })
  });
  if (!res.ok) throw new InterviewApiError(res.status, await parseErrorBody(res));
  return res.json() as Promise<VoiceConnectResponse>;
}

export class InterviewApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = 'InterviewApiError';
  }
}
